/**
 * CSV exports over the analysis CSV builders, ported from loader-export.js
 * exportCSV and exportBudgetCSV. The pure part turns a snapshot of the loader
 * store (dataset, results, timeframe, active sets) into CSV text; the export
 * functions read the store, warn when there is nothing to export and download.
 */
import { toast } from '@/components/ui/toast';
import {
  buildAnalysisCsv,
  buildBudgetCsv,
  conflictRows,
  contactWindowRows,
  matrixRows,
  summaryRows,
  toCsv,
  utilisationRow,
} from '../analysis/exportCsv';
import type { AnalysisResults, BudgetProjection } from '../analysis/types';
import { useLoader, type Timeframe } from '../store';
import type { Antenna, Mission, NetworkDataset } from '../types';
import { dateStamp, downloadBlob } from './download';
import { useExportState } from './exportStore';

export const CSV_MIME = 'text/csv;charset=utf-8;';

/** What an export needs from the store at the moment it runs. */
export interface ExportSnapshot {
  network: string;
  dataset: NetworkDataset;
  results: AnalysisResults;
  timeframe: Timeframe;
  /** The what-if active missions, in dataset order. */
  missions: Mission[];
  /** The what-if active antennas, in dataset order. */
  antennas: Antenna[];
}

export function activeMissions(dataset: NetworkDataset, activeIds: string[]): Mission[] {
  const active = new Set(activeIds);
  return dataset.missions.filter((m) => active.has(m.id));
}

export function activeAntennas(dataset: NetworkDataset, activeIds: string[]): Antenna[] {
  const active = new Set(activeIds);
  return dataset.antennas.filter((a) => active.has(a.id));
}

export function snapshotFrom(state: {
  network: string;
  dataset: NetworkDataset | null;
  results: AnalysisResults | null;
  timeframe: Timeframe;
  activeMissionIds: string[];
  activeAntennaIds: string[];
}): ExportSnapshot | null {
  if (!state.dataset || !state.results) return null;
  return {
    network: state.network,
    dataset: state.dataset,
    results: state.results,
    timeframe: state.timeframe,
    missions: activeMissions(state.dataset, state.activeMissionIds),
    antennas: activeAntennas(state.dataset, state.activeAntennaIds),
  };
}

/** The store's current snapshot, or null (with a warning toast) when no analysis has run. */
export function snapshotFromStore(): ExportSnapshot | null {
  const snapshot = snapshotFrom(useLoader.getState());
  if (!snapshot) toast('warn', 'Nothing to export: run an analysis first.');
  return snapshot;
}

/** network-loading-<network>-<date>[-<kind>].<ext>, the original's name with an optional section suffix. */
export function exportFilename(network: string, kind: string | null, ext: string, nowMs: number = Date.now()): string {
  return `network-loading-${network}-${dateStamp(nowMs)}${kind ? `-${kind}` : ''}.${ext}`;
}

export function matrixCsv(s: ExportSnapshot): string {
  return toCsv(matrixRows(s.results, s.missions, s.antennas));
}

export function utilisationCsv(s: ExportSnapshot): string {
  return toCsv([['Antenna', 'Utilization'], ...utilisationPairs(s)]);
}

/** One row per antenna from the builders' utilisation row (antenna short name, percentage or '--'). */
function utilisationPairs(s: ExportSnapshot): (string | number | null | undefined)[][] {
  const row = utilisationRow(s.results, s.antennas);
  // row is ['Utilization', '', ...one cell per antenna]
  return s.antennas.map((a, i) => [a.shortName, row[i + 2]]);
}

export function conflictsCsv(s: ExportSnapshot): string {
  return toCsv(conflictRows(s.results, s.missions, s.antennas));
}

export function contactWindowsCsv(s: ExportSnapshot): string {
  return toCsv(contactWindowRows(s.results, s.missions, s.antennas));
}

export function summaryCsv(s: ExportSnapshot): string {
  return toCsv(summaryRows(s.results));
}

/** The full analysis export (matrix, utilisation, conflicts, contact windows, summary), as the original E key. */
export function analysisCsv(s: ExportSnapshot): string {
  return buildAnalysisCsv(s.results, s.missions, s.antennas);
}

export function budgetCsv(projection: BudgetProjection, antennas: Antenna[]): string {
  return buildBudgetCsv(projection, antennas);
}

function exportSection(kind: string | null, build: (s: ExportSnapshot) => string): boolean {
  const snapshot = snapshotFromStore();
  if (!snapshot) return false;
  downloadBlob(build(snapshot), exportFilename(snapshot.network, kind, 'csv'), CSV_MIME);
  return true;
}

export function exportMatrixCsv(): boolean {
  return exportSection('matrix', matrixCsv);
}

export function exportUtilisationCsv(): boolean {
  return exportSection('utilisation', utilisationCsv);
}

export function exportConflictsCsv(): boolean {
  return exportSection('conflicts', conflictsCsv);
}

export function exportContactWindowsCsv(): boolean {
  return exportSection('contact-windows', contactWindowsCsv);
}

export function exportSummaryCsv(): boolean {
  return exportSection('summary', summaryCsv);
}

/** The E key: the complete analysis CSV named network-loading-<network>-<date>.csv. */
export function exportCsvQuick(): boolean {
  return exportSection(null, analysisCsv);
}

/** budget-projection-<date>.csv from the last projection the Budget panel published. */
export function exportBudgetCsv(): boolean {
  const projection = useExportState.getState().budgetProjection;
  if (!projection) {
    toast('warn', 'No projection to export: generate a budget projection first.');
    return false;
  }
  const dataset = useLoader.getState().dataset;
  downloadBlob(budgetCsv(projection, dataset?.antennas ?? []), `budget-projection-${dateStamp()}.csv`, CSV_MIME);
  return true;
}
